/* eslint-disable complexity */
/* eslint-disable max-statements */
import { Blueprint, BlueprintData } from '@rta/rta-blueprint-library';

import { SaveDirtyKitchenLocally } from '@features/customer/helpers';
import { sanitizeBlueprintData } from './sanitize-blueprint-data';

export type HydrateBlueprintParams = {
	blueprintData: BlueprintData;
	/** Position in the loaded page, quoted in the console on a bad entry. */
	entryIndex: number;
	userEmail?: string;
	/** Locally autosaved drafts keyed by blueprint id — a consumed entry is deleted from this map. */
	autosaveEntries: Record<string, SaveDirtyKitchenLocally>;
	/** Mint a temp draft when the blueprint carries none, because the caller intends to edit it. */
	forceDraft: boolean;
};

export type HydratedBlueprint = {
	blueprint: Blueprint;
	isDirty: boolean;
};

/**
 * Turns one API design record into a Blueprint, with its locally autosaved draft folded back in.
 * Shared by the first page load and by every Load More, so a design pulled in later is built exactly
 * like one that arrived with the first page.
 * @param params the record, the autosave map and whether the caller needs a draft
 * @returns the blueprint and whether it carries unsaved local changes, or null when the record is unusable
 */
// eslint-disable-next-line max-lines-per-function
export const hydrateBlueprint = (params: HydrateBlueprintParams): HydratedBlueprint | null => {
	const { blueprintData, entryIndex, userEmail, autosaveEntries, forceDraft } = params;

	// Step 1: Validate blueprintData is a non-null object
	if (!blueprintData || typeof blueprintData !== 'object') {
		console.error(
			`[Blueprint Load] Entry ${entryIndex}: received non-object data.`,
			{ type: typeof blueprintData, value: blueprintData }
		);
		return null;
	}

	// Step 2: Check for empty object (API error symptom)
	const dataKeys = Object.keys(blueprintData);
	if (dataKeys.length === 0) {
		console.error(
			`[Blueprint Load] Entry ${entryIndex}: received empty object from API.`,
			'This typically means the API failed to convert the blueprint payload.',
			'Check the API server logs for "Error converting raw blueprint payload" or blob download failures.'
		);
		return null;
	}

	// Step 3: Validate required identity fields
	const { id, submissionId } = blueprintData;
	if (!id && !submissionId) {
		console.error(
			`[Blueprint Load] Entry ${entryIndex}: missing required identity fields (id and submissionId are both falsy).`,
			{
				receivedKeys: dataKeys,
				email: blueprintData.email ?? '(missing)',
				schemaVersion: blueprintData.schemaVersion ?? '(missing)',
				hasKitchens: !!blueprintData.kitchens,
				kitchenCount: Array.isArray(blueprintData.kitchens) ? blueprintData.kitchens.length : '(not an array)'
			}
		);
		return null;
	}

	// Step 4: Sanitize blueprint data to remove null values from arrays that should contain objects
	// This prevents errors when the blueprint library tries to create objects from null values
	const sanitizedData = sanitizeBlueprintData(blueprintData);

	// Step 5: Construct Blueprint instance
	let blueprint: Blueprint;
	try {
		blueprint = new Blueprint({ dataIn: sanitizedData, userEmail });
	} catch (constructErr) {
		console.error(
			`[Blueprint Load] Entry ${entryIndex}: Blueprint construction threw.`,
			{
				error: constructErr instanceof Error
					? { name: constructErr.name, message: constructErr.message, stack: constructErr.stack }
					: constructErr,
				sanitizedData
			}
		);
		throw constructErr;
	}

	const existingDraft = blueprint.kitchens.findVersion(null);
	const draftKitchen = autosaveEntries[blueprint.id];
	let isDirty = false;

	// a locally autosaved draft replaces whatever draft the blob carries
	if (draftKitchen) {
		isDirty = true;

		if (existingDraft) blueprint.kitchens.delete(existingDraft.id);

		const k = blueprint.kitchens.newKitchen(draftKitchen.data);
		k.isLocal = true;
		delete autosaveEntries[blueprint.id];
	} else if (existingDraft === null && forceDraft) {
		// newVersionFromLatest creates a draft from the latest version
		const kv = blueprint.kitchens.newVersionFromLatest();
		if (kv) kv.isTemp = true;
	}

	return { blueprint, isDirty };
};
